import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo/routedClient";
import { WorkspacesDocument, WorkspacesQuery, WorkspacesQueryVariables } from "@gql/sites-api/graphql";

import { siteStore } from "../store";
import { subscribe } from "valtio/vanilla";

const SELECTED_WORKSPACE_LOCAL_STORAGE_KEY = "selectedWorkspaceId";
const LEGACY_SELECTED_PROJECT_LOCAL_STORAGE_KEY = "selectedProjectId";

/**
 * Reads the persisted workspace id using the current key, and falls back to the
 * legacy `selectedProjectId` key from before the projects→workspaces rename. Without
 * that fallback, existing users would load the app with no selection after deploy;
 * when we find a legacy value we copy it to the new key and remove the old entry.
 */
function readWorkspaceIdFromLocalStorage(): string | null {
  const fromNew = localStorage.getItem(SELECTED_WORKSPACE_LOCAL_STORAGE_KEY);
  if (fromNew) return fromNew;
  const fromLegacy = localStorage.getItem(LEGACY_SELECTED_PROJECT_LOCAL_STORAGE_KEY);
  if (fromLegacy) {
    localStorage.setItem(SELECTED_WORKSPACE_LOCAL_STORAGE_KEY, fromLegacy);
    localStorage.removeItem(LEGACY_SELECTED_PROJECT_LOCAL_STORAGE_KEY);
    return fromLegacy;
  }
  return null;
}

function persistSelectedWorkspaceIdToLocalStorage() {
  if (siteStore.selectedWorkspaceId) {
    localStorage.setItem(SELECTED_WORKSPACE_LOCAL_STORAGE_KEY, siteStore.selectedWorkspaceId);
    localStorage.removeItem(LEGACY_SELECTED_PROJECT_LOCAL_STORAGE_KEY);
  }
}

/**
 * We want to update the selected workspace in localStorage whenever the selected
 * workspace in store changes OR when the tab is focussed. The latter effect is
 * necessary to ensure that when refreshing the current tab, the workspace does
 * not change unexpectedly.
 *
 * @todo handle current workspace being archived from another tab
 */
subscribe(siteStore, persistSelectedWorkspaceIdToLocalStorage);
addEventListener("focus", persistSelectedWorkspaceIdToLocalStorage);

/**
 * Reconciles the selected workspace from network, localStorage, and store.
 *
 * Orders of precedence:
 *   1. Workspace in store (validated against workspaces from network)
 *   2. Workspace in localStorage (validated against workspaces from network)
 *   3. Last opened workspace from network
 *
 * @todo handle no workspaces found for user
 */
export async function loadSelectedWorkspace() {
  try {
    const {
      data: { workspaces },
    } = await routedClient.query<WorkspacesQuery, WorkspacesQueryVariables>({
      query: WorkspacesDocument,
      fetchPolicy: "network-only",
      context: {
        endpoint: LandTechEndpoints.Sites,
      },
    });

    /**
     * If a workspace is already in memory prioritise that over the local storage
     * value. This allows for multiple tabs to use distinct workspaces.
     */
    const workspaceIdFromMemory = siteStore.selectedWorkspaceId;
    const workspaceIdFromLocalStorage = readWorkspaceIdFromLocalStorage();
    const workspaceIdToUse = workspaceIdFromMemory || workspaceIdFromLocalStorage;
    const selectedWorkspace = workspaces.find((workspace) => workspace.id === workspaceIdToUse);

    siteStore.selectedWorkspaceId = selectedWorkspace?.id ?? workspaces[0]?.id ?? null;
  } catch (error) {
    console.error(error);
  }
}
